import React, { useEffect, useState } from 'react';
import { isTelegramWebApp } from '../../utils/telegram';

interface TelegramInitProps {
  children: React.ReactNode;
  onInit?: (isTelegram: boolean, username?: string) => void;
}

export const TelegramInit: React.FC<TelegramInitProps> = ({ children, onInit }) => {
  const [username, setUsername] = useState<string>('');
  const [isTelegramEnv, setIsTelegramEnv] = useState(false);

  useEffect(() => {
    const isTelegram = isTelegramWebApp();
    setIsTelegramEnv(isTelegram);

    let currentUsername = '';

    if (isTelegram) {
      // @ts-ignore
      const tg = window.Telegram?.WebApp;
      if (tg) {
        tg.ready();

        // Отступ сверху: 64px в полноэкранном режиме, 32px в остальных
        const updateTopPadding = () => {
          const isFullscreen = tg.isFullscreen;
          document.documentElement.style.setProperty(
            '--content-padding-top',
            isFullscreen ? 'calc(var(--tg-safe-area-inset-top, 0px) + 64px)' : '32px'
          );
        };
        const schedulePaddingRecalc = () => {
          updateTopPadding();
          // iOS обновляет safe-area/viewport не синхронно — дожимаем в следующих тиках.
          requestAnimationFrame(() => updateTopPadding());
          setTimeout(() => updateTopPadding(), 50);
        };

        schedulePaddingRecalc();
        tg.onEvent('fullscreen_changed', schedulePaddingRecalc);
        // События приходят на разных платформах/версиях клиента, но вместе ловят момент,
        // когда safe-area/viewport реально пересчитались.
        tg.onEvent('viewportChanged', schedulePaddingRecalc);
        tg.onEvent('safe_area_changed', schedulePaddingRecalc);
        tg.onEvent('content_safe_area_changed', schedulePaddingRecalc);

        // Раскрытие Mini App. Особенно важно при входе через Menu Button — там часто стартует
        // compact и в BotFather нельзя задать Launch Mode.
        try {
          tg.expand?.();
          const isMobile = tg.platform === 'ios' || tg.platform === 'android';
          if (isMobile) {
            // @ts-ignore
            tg.requestFullscreen?.();
            schedulePaddingRecalc();
          }
        } catch (e) {
          console.warn('Unable to expand/requestFullscreen:', e);
        }

        const user = tg.initDataUnsafe?.user;
        if (user?.username) currentUsername = user.username;
        else if (user?.first_name) currentUsername = user.first_name;
        else currentUsername = `user_${user?.id || 'unknown'}`;
      }
    }

    setUsername(currentUsername);
    onInit?.(isTelegram, currentUsername);
  }, [onInit]);

  useEffect(() => {
    if (!isTelegramEnv) return;
    // @ts-ignore
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    tg.BackButton.onClick(() => window.history.back());

    const checkPath = () => {
      const isRoot = window.location.pathname === '/';
      if (isRoot) tg.BackButton.hide();
      else tg.BackButton.show();
    };

    checkPath();

    window.addEventListener('popstate', checkPath);

    // SPA-навигация history.push/replaceState не триггерит popstate — оборачиваем,
    // чтобы кнопка «Назад» в TG-клиенте корректно показывалась/скрывалась.
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function (...args) {
      const result = originalPushState.apply(this, args);
      setTimeout(checkPath, 0);
      return result;
    };
    window.history.replaceState = function (...args) {
      const result = originalReplaceState.apply(this, args);
      setTimeout(checkPath, 0);
      return result;
    };

    return () => {
      window.removeEventListener('popstate', checkPath);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [isTelegramEnv]);

  return <TelegramContext.Provider value={{ username }}>{children}</TelegramContext.Provider>;
};

export const TelegramContext = React.createContext<{ username: string }>({ username: '' });
